import {
  AssetChatSubject,
  LocationInput,
  MediaData,
  MediaPickerComponent,
  MediaType,
  messageFormatMarkers,
  PickerMode,
  GeoJsonType,
  Severity,
  useCreateChatActivity,
  useShareLocation,
} from '@gripp/shared-logic';
import { QueryKey } from '@tanstack/react-query';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Pressable, Text, View } from 'react-native';
import { Menu } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { severityToVariant, TypeIcon } from '../crop-circle/Components/Type';
import {
  ArrowUpIcon,
  BoldIcon,
  BulletListIcon,
  DocumentIcon,
  ImageIcon,
  ItalicIcon,
  LinkIcon,
  LocationIcon,
  OrderedListIcon,
  StrikethroughIcon,
  TextIcon,
  UnderlineIcon,
} from '../crop-circle/icons';
import { Icon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { RenderMediaItem } from '../media/renderMediaItem';
import { useTypedMedia } from '../media/useTypedMedia';
import { MentionInput } from '../mentionInputField';
import { useMentionTextFormatting } from '../mentionInputField/useMentionTextFormatting';

/**
 * The send-as menu, and **the only place a human-readable label meets a
 * `Severity`**. `ReportIssueInput.severity` is `String!` server-side while
 * `Issue.severity` reads back as an enum, so a label that reached the wire
 * would write a document every later read fails on (gripp-9ee). Keeping the
 * pairing in one table — and passing the `Severity` member, never the label —
 * is what makes that unrepresentable.
 */
const SEND_AS_OPTIONS: { severity: Severity; labelKey: string }[] = [
  { severity: Severity.Red, labelKey: 'issue.types.urgent' },
  { severity: Severity.Yellow, labelKey: 'issue.types.pending' },
  { severity: Severity.Green, labelKey: 'issue.types.solved' },
];

const MIN_CARD_HEIGHT = 124;

/** Reference geometry: 16px glyphs in 20px boxes, 24px apart. */
const GLYPH_BOX = 20;
const GLYPH_GAP = spacing['2xl'];

export type AssetChatComposerProps = {
  /** The asset and workspace a post is filed against. */
  subject: Pick<AssetChatSubject, 'assetId' | 'workspaceId'>;
  /** Named in the placeholder ("Message 1795-9…"). */
  assetName?: string;
  /** The feed's query key, so a post shows before the server answers. */
  feedCacheKey?: QueryKey;
  /** Platform media picker; the host supplies web's or native's. */
  mediaPicker: MediaPickerComponent;
  testID?: string;
};

/**
 * The asset chat's composer. Writing here creates the same `Issue` the classic
 * activity list shows — see `useCreateChatActivity`, which is where that
 * equivalence is enforced.
 *
 * Owns its own layout rather than wrapping `IssueFooter`: the chat design is a
 * **card** — input on top, a toolbar row beneath it, a split send control at
 * the bottom right — where the web `IssueFooter` is still the legacy
 * add-button-and-pill row awaiting its own redesign pass. Building on that row
 * produced a composer that did not read as the design at all. The mechanics
 * are still shared, though: `useTypedMedia` stages media, `MentionInput`
 * handles text and mentions, and `useMentionTextFormatting` drives the
 * text-style row with the same markers `issueFooter` and `issueFooterMobile`
 * write, so a message composed here is byte-identical to one composed there.
 *
 * The bottom bar has two modes that crossfade in one zone, per the reference:
 * *attach* (add image, add document, share location) and *text* (the style
 * markers). Dictation and Ask Gripp AI are drawn in the reference but are
 * out of scope for v1, so their glyphs are omitted rather than shown dead.
 */
export const AssetChatComposer = ({
  subject,
  assetName,
  feedCacheKey,
  mediaPicker,
  testID,
}: AssetChatComposerProps) => {
  const { t } = useTranslation();
  const MediaPicker = mediaPicker;
  const { createChatActivity } = useCreateChatActivity({
    subject,
    feedCacheKey,
  });
  const { shareLocation: requestLocation } = useShareLocation();
  const { typedMedia, handleAdd, handleDelete, handleUploadProgress, reset } =
    useTypedMedia<MediaData>([]);

  const [message, setMessage] = useState('');
  const [sendAsSeverity, setSendAsSeverity] = useState<Severity>();
  const [menuVisible, setMenuVisible] = useState(false);
  const [location, setLocation] = useState<LocationInput>();
  const [isTextMode, setIsTextMode] = useState(false);
  const [isUploading, setUploading] = useState(false);
  // Guards a second send while the first is still in flight — the draft is
  // cleared optimistically, so without this a double press could post twice.
  const [isSending, setIsSending] = useState(false);
  const [inputHeight, setInputHeight] = useState(24);

  const imagePickerRef = useRef<(() => void) | null>(null);
  const documentPickerRef = useRef<(() => void) | null>(null);

  const { selection, onSelectionChange, wrapSelection, insertLink, prefixLines } =
    useMentionTextFormatting({ value: message, onChange: setMessage });

  // Trimmed, unlike the legacy footer's untrimmed check: whitespace alone is
  // not a message, and the send control's colour is derived from this.
  const canSend =
    (message.trim().length > 0 || typedMedia.length > 0) &&
    !isUploading &&
    !isSending;

  const sendAsOption = SEND_AS_OPTIONS.find(
    (option) => option.severity === sendAsSeverity
  );

  const send = async () => {
    if (!canSend || isSending) return;
    const outgoing = message;
    const outgoingMedia = typedMedia;
    const outgoingSeverity = sendAsSeverity;
    const outgoingLocation = location;
    // Cleared immediately, the way a chat composer should feel — but restored
    // below if the post fails, because otherwise "try again" means retyping
    // the message and re-attaching every file.
    setMessage('');
    reset();
    setIsSending(true);
    try {
      await createChatActivity({
        message: outgoing,
        severity: outgoingSeverity,
        media: outgoingMedia,
        location: outgoingLocation,
      });
      // Both modifiers are per-post, not sticky: the next message in a
      // conversation is an ordinary message unless the author says otherwise,
      // and a stale "Posting as Urgent" would file the wrong record silently.
      setSendAsSeverity(undefined);
      setLocation(undefined);
    } catch (error) {
      // Put the draft back exactly as it was, modifiers included, so the
      // author can retry rather than reconstruct.
      setMessage(outgoing);
      outgoingMedia.forEach((item) => handleAdd(item));
      setSendAsSeverity(outgoingSeverity);
      setLocation(outgoingLocation);
      Toast.show({ type: 'error', text1: t('chat.composer.postFailed') });
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Takes a FRESH fix rather than reading `useCurrentLocation`'s cache. That
   * cache is written by a location *watcher*, which never runs on web — so the
   * cached read resolved undefined every time and the control could only ever
   * report failure. Sharing a location is user-initiated, so asking for the
   * permission at the moment of the tap is also the honest place to ask.
   */
  const shareLocation = async () => {
    if (location) {
      setLocation(undefined);
      return;
    }

    const result = await requestLocation();

    if (result.status === 'denied') {
      Toast.show({
        type: 'error',
        text1: t('issue.location.deniedTitle'),
        text2: t('issue.location.deniedMessage'),
      });
      return;
    }

    if (result.status === 'unavailable') {
      Toast.show({
        type: 'error',
        text1: t('issue.location.unavailableTitle'),
        text2: t('issue.location.unavailableMessage'),
      });
      return;
    }

    // GeoJSON order is [longitude, latitude] — the reverse of how the fix
    // reports it, and of how anyone says it out loud.
    setLocation({
      type: GeoJsonType.Point,
      coordinates: [result.longitude, result.latitude],
    });
  };

  const attachButtons: {
    label: string;
    icon: ReactNode;
    onPress: () => void;
    testID?: string;
  }[] = [
      {
        label: t('chat.composer.addImage'),
        icon: <ImageIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => imagePickerRef.current?.(),
        testID: testID ? `${testID}-add-image` : undefined,
      },
      {
        label: t('chat.composer.addDocument'),
        icon: <DocumentIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => documentPickerRef.current?.(),
        testID: testID ? `${testID}-add-document` : undefined,
      },
      {
        label: location
          ? t('chat.composer.removeLocation')
          : t('chat.composer.shareLocation'),
        icon: (
          <LocationIcon
            size={16}
            color={
              location ? Colors.role.brandOnSurface : Colors.role.textSecondary
            }
          />
        ),
        onPress: shareLocation,
        testID: testID ? `${testID}-share-location` : undefined,
      },
    ];

  // Same order and markers as issueFooterMobile's row — the point is that both
  // surfaces author one syntax, not two.
  const textButtons: { label: string; icon: ReactNode; onPress: () => void }[] =
    [
      {
        label: t('issue.footer.bold'),
        icon: <BoldIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.bold.open,
            messageFormatMarkers.bold.close
          ),
      },
      {
        label: t('issue.footer.italic'),
        icon: <ItalicIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.italic.open,
            messageFormatMarkers.italic.close
          ),
      },
      {
        label: t('issue.footer.underline'),
        icon: <UnderlineIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.underline.open,
            messageFormatMarkers.underline.close
          ),
      },
      {
        label: t('issue.footer.strikethrough'),
        icon: <StrikethroughIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.strikethrough.open,
            messageFormatMarkers.strikethrough.close
          ),
      },
      {
        label: t('issue.footer.link'),
        icon: <LinkIcon size={16} color={Colors.role.textSecondary} />,
        onPress: insertLink,
      },
      {
        label: t('issue.footer.orderedList'),
        icon: <OrderedListIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => prefixLines((index) => `${index + 1}. `),
      },
      {
        label: t('issue.footer.bulletList'),
        icon: <BulletListIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => prefixLines(() => '- '),
      },
    ];

  return (
    <View style={styles.wrapper} testID={testID}>
      <View style={[styles.card, { minHeight: MIN_CARD_HEIGHT }]}>
        {(sendAsOption || location) && (
          <View style={styles.chipRow}>
            {sendAsOption && (
              <View
                style={styles.chip}
                testID={testID ? `${testID}-send-as-chip` : undefined}
              >
                <TypeIcon
                  variant={severityToVariant[sendAsOption.severity]}
                  size={12}
                />
                <Text style={styles.chipText} numberOfLines={1}>
                  {t('chat.composer.postingAs', {
                    type: t(sendAsOption.labelKey),
                  })}
                </Text>
                <Pressable
                  onPress={() => setSendAsSeverity(undefined)}
                  accessibilityRole="button"
                  accessibilityLabel={t('chat.composer.postAsMessageInstead')}
                  testID={testID ? `${testID}-clear-send-as` : undefined}
                >
                  <Icon name="x" size={12} color={Colors.role.textSoft} />
                </Pressable>
              </View>
            )}
            {location && (
              <View
                style={styles.chip}
                testID={testID ? `${testID}-location-chip` : undefined}
              >
                <LocationIcon size={12} color={Colors.role.textSoft} />
                <Text style={styles.chipText} numberOfLines={1}>
                  {t('chat.composer.locationAttached')}
                </Text>
                <Pressable
                  onPress={() => setLocation(undefined)}
                  accessibilityRole="button"
                  accessibilityLabel={t('chat.composer.removeLocation')}
                  testID={testID ? `${testID}-clear-location` : undefined}
                >
                  <Icon name="x" size={12} color={Colors.role.textSoft} />
                </Pressable>
              </View>
            )}
          </View>
        )}

        <View style={styles.inputArea}>
          <MentionInput
            inputFieldText={message}
            setInputFieldText={setMessage}
            setIsMultiline={() => undefined}
            inputHeight={inputHeight}
            setInputHeight={setInputHeight}
            send={() => void send()}
            placeholder={
              assetName
                ? t('chat.composer.placeholder', { asset: assetName })
                : t('chat.composer.placeholderFallback')
            }
            suggestionsPosition="top"
            onSelectionChange={onSelectionChange}
            selection={selection}
          />
        </View>

        {typedMedia.length > 0 && (
          <View
            style={styles.mediaRow}
            testID={testID ? `${testID}-media` : undefined}
          >
            {typedMedia.map((item) => (
              <RenderMediaItem
                key={item.id}
                mediaItem={item}
                deleteMediaItem={handleDelete}
                canDelete
                displayWidth={72}
              />
            ))}
          </View>
        )}

        <View style={styles.bottomBar}>
          <View style={styles.modeZone}>
            {/* Outside the crossfade — it is the switch between the two modes,
                not a member of either. */}
            <Pressable
              onPress={() => setIsTextMode((current) => !current)}
              style={styles.glyph}
              accessibilityRole="button"
              accessibilityState={{ selected: isTextMode }}
              accessibilityLabel={t('issue.footer.textStyles')}
              testID={testID ? `${testID}-text-mode` : undefined}
            >
              <TextIcon
                size={16}
                color={
                  isTextMode
                    ? Colors.role.brandOnSurface
                    : Colors.role.textSecondary
                }
              />
            </Pressable>

            <CrossfadeGroup
              active={!isTextMode}
              from="left"
              gap={GLYPH_GAP}
              testID={testID ? `${testID}-attach-bar` : undefined}
            >
              {attachButtons.map((button) => (
                <Pressable
                  key={button.testID ?? button.label}
                  onPress={button.onPress}
                  style={styles.glyph}
                  accessibilityRole="button"
                  accessibilityLabel={button.label}
                  // Both groups stay mounted for the crossfade, so the hidden
                  // one has to leave the tab order too — opacity 0 and
                  // pointerEvents alone still let a keyboard reach it.
                  focusable={!isTextMode}
                  testID={button.testID}
                >
                  {button.icon}
                </Pressable>
              ))}
            </CrossfadeGroup>

            <CrossfadeGroup
              active={isTextMode}
              from="right"
              overlay
              gap={spacing.md}
              testID={testID ? `${testID}-text-bar` : undefined}
            >
              {textButtons.map((button) => (
                <Pressable
                  key={button.label}
                  onPress={button.onPress}
                  style={styles.glyph}
                  accessibilityRole="button"
                  accessibilityLabel={button.label}
                  focusable={isTextMode}
                >
                  {button.icon}
                </Pressable>
              ))}
            </CrossfadeGroup>
          </View>

          {/* One pill, both halves: dark at rest, brand once there is
              something to send. */}
          <View style={[styles.sendPill, canSend && styles.sendPillActive]}>
            <Menu
              visible={menuVisible}
              onDismiss={() => setMenuVisible(false)}
              contentStyle={styles.menu}
              anchor={
                <Pressable
                  onPress={() => setMenuVisible(true)}
                  style={styles.sendHalf}
                  accessibilityRole="button"
                  accessibilityLabel={t('chat.composer.sendAs')}
                  testID={testID ? `${testID}-send-as` : undefined}
                >
                  <Icon
                    name={menuVisible ? 'Chevron Up' : 'Chevron Down'}
                    size={16}
                    color={Colors.role.textOnInverse}
                  />
                </Pressable>
              }
            >
              {/* Only the way back from a type — in the default mode it would
                  be a row that does nothing. */}
              {sendAsSeverity && (
                <SendAsRow
                  label={t('chat.composer.asMessage')}
                  onPress={() => {
                    setMenuVisible(false);
                    setSendAsSeverity(undefined);
                  }}
                  testID={testID ? `${testID}-send-as-message` : undefined}
                />
              )}
              {SEND_AS_OPTIONS.map((option) => (
                <SendAsRow
                  key={option.severity}
                  label={t(option.labelKey)}
                  severity={option.severity}
                  isSelected={option.severity === sendAsSeverity}
                  onPress={() => {
                    setMenuVisible(false);
                    setSendAsSeverity(option.severity);
                  }}
                  testID={
                    testID ? `${testID}-send-as-${option.severity}` : undefined
                  }
                />
              ))}
            </Menu>

            <View style={styles.sendSeam} />

            <Pressable
              onPress={() => void send()}
              disabled={!canSend}
              style={styles.sendHalf}
              accessibilityRole="button"
              accessibilityState={{ disabled: !canSend }}
              accessibilityLabel={
                sendAsOption
                  ? t('chat.composer.sendAsType', {
                      type: t(sendAsOption.labelKey),
                    })
                  : t('chat.composer.send')
              }
              testID={testID ? `${testID}-send` : undefined}
            >
              <ArrowUpIcon size={18} color={Colors.role.textOnInverse} />
            </Pressable>
          </View>
        </View>
      </View>

      {/* Mounted offscreen purely to hand back their openers — the visible
          controls are the glyphs above, so the picker's own trigger (and its
          negative margins) never render. */}
      <View style={styles.headlessPicker} pointerEvents="none">
        <MediaPicker
          mode={PickerMode.PICKER}
          acceptedMediaTypes={[MediaType.Image, MediaType.Video]}
          setMedia={handleAdd}
          setProgress={handleUploadProgress}
          setUploading={setUploading}
          workspaceId={subject.workspaceId}
          onPickerReady={(opener) => {
            imagePickerRef.current = opener;
          }}
        />
        <MediaPicker
          mode={PickerMode.ATTACHMENT}
          acceptedMediaTypes={[MediaType.Document]}
          setMedia={handleAdd}
          setProgress={handleUploadProgress}
          setUploading={setUploading}
          workspaceId={subject.workspaceId}
          onPickerReady={(opener) => {
            documentPickerRef.current = opener;
          }}
        />
      </View>
    </View>
  );
};

/**
 * One half of the bottom bar's two modes. The attach group stays in flow so it
 * reserves the row's width; the text group overlays it, so the two swap in
 * place instead of the bar resizing under the cursor.
 */
const CrossfadeGroup = ({
  active,
  from,
  overlay,
  gap,
  children,
  testID,
}: {
  active: boolean;
  from: 'left' | 'right';
  overlay?: boolean;
  /** Attach sits at 24, the denser text row at 12 — both from the reference. */
  gap: number;
  children: ReactNode;
  testID?: string;
}) => {
  const progress = useRef(new Animated.Value(active ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: active ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [active, progress]);

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [from === 'left' ? -8 : 8, 0],
  });

  return (
    <Animated.View
      style={[
        styles.glyphGroup,
        overlay && styles.glyphGroupOverlay,
        { gap, opacity: progress, transform: [{ translateX }] },
      ]}
      pointerEvents={active ? 'auto' : 'none'}
      // The inactive mode is invisible but still mounted, so it is hidden
      // from assistive technology as well as from the pointer. Its buttons
      // additionally drop out of the tab order (see `focusable` on each).
      accessibilityElementsHidden={!active}
      importantForAccessibility={
        active ? 'auto' : 'no-hide-descendants'
      }
      testID={testID}
    >
      {children}
    </Animated.View>
  );
};

/**
 * One row of the send-as menu. Mirrors `messageActionMenu`'s rows rather than
 * Paper's `Menu.Item`, which reserves a wide fixed icon column.
 */
const SendAsRow = ({
  label,
  severity,
  isSelected,
  onPress,
  testID,
}: {
  label: string;
  severity?: Severity;
  isSelected?: boolean;
  onPress: () => void;
  testID?: string;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: !!isSelected }}
      testID={testID}
      style={[styles.menuItem, hovered && styles.menuItemHovered]}
    >
      {severity ? (
        <TypeIcon variant={severityToVariant[severity]} size={16} />
      ) : (
        <Icon name="Comment" size={16} color={Colors.role.textStrong} />
      )}
      <Text style={[styles.menuItemTitle, isSelected && styles.menuItemSelected]}>
        {label}
      </Text>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  wrapper: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  // The composer is a card, not a row — input on top, controls beneath.
  card: {
    backgroundColor: role.surfaceRaised,
    borderRadius: tokens.borderRadius.sm,
    padding: spacing.lg,
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
  },
  chipText: {
    ...tokens.typography.label.medium,
    color: role.textStrong,
    flexShrink: 1,
  },
  inputArea: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  mediaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  bottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeZone: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    flexShrink: 1,
    // The toggle is one glyph away from the group it switches, same as the
    // spacing within the group itself.
    gap: GLYPH_GAP,
  },
  glyphGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Starts where the attach group starts, so the two modes swap in place
  // rather than sliding sideways: the toggle's width plus the zone's gap.
  glyphGroupOverlay: {
    position: 'absolute',
    left: GLYPH_BOX + GLYPH_GAP,
    top: 0,
    bottom: 0,
  },
  glyph: {
    width: GLYPH_BOX,
    height: GLYPH_BOX,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // One pill split into two halves by a hairline seam, per the reference.
  sendPill: {
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: role.surfaceInverse,
  },
  sendPillActive: {
    backgroundColor: tokens.colors.brand.core,
  },
  sendHalf: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendSeam: {
    width: 1,
    marginVertical: 6,
    backgroundColor: role.textOnInverse,
    opacity: 0.4,
  },
  menu: {
    backgroundColor: role.canvas,
    minWidth: 200,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: 1,
    borderColor: role.borderControl,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  menuItem: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: tokens.borderRadius.sm,
  },
  menuItemHovered: {
    backgroundColor: role.surfaceHover,
  },
  menuItemTitle: {
    ...tokens.typography.label.medium,
    color: role.textStrong,
  },
  menuItemSelected: {
    color: role.brandOnSurface,
  },
  headlessPicker: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 0,
    height: 0,
    opacity: 0,
  },
}));
